/*
================================================================================
|                                                                              |
|  FILE: src/sections/ecorevivepage/EcoReviveProjectOverview.js                |
|  PURPOSE: EcoRevive Project Overview section with unique design and content.|
|                                                                              |
================================================================================
*/
import React from 'react';
import ImageSlider from '../../components/common/ImageSlider';

export default function EcoReviveProjectOverview() {
    const ecoReviveImages = [
        {
            src: "/images/Website_Impact Section1.jpg",
            alt: "Environmental restoration project overview",
            overlay: {
                title: "Project Overview",
                subtitle: "EcoRevive Initiative"
            }
        },
        {
            src: "/images/Website_Impact Section2.jpg",
            alt: "Land restoration and soil remediation",
            overlay: {
                title: "Land Restoration",
                subtitle: "Soil Remediation"
            }
        },
        {
            src: "/images/Website_Impact Section3.jpg",
            alt: "Water purification and river restoration",
            overlay: {
                title: "Pra River",
                subtitle: "Ghana, West Africa"
            }
        },
        {
            src: "/images/Website_Impact Section4.jpg",
            alt: "Community development and green hubs",
            overlay: {
                title: "Community Impact",
                subtitle: "Green Hubs Network"
            }
        }
    ];

    return (
        <section className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold mb-4">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        Flagship Initiative
                    </div>
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">
                        Project EcoRevive: Environmental Recovery in Action
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Healing the scars of illegal mining in the Apamprama forest reserve and surrounding communities in Ghana
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-8 mb-16">
                                            <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-blue-500">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm-1-13h2v6h-2V7zm0 8h2v2h-2v-2z"/>
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Water Purification</h3>
                        <p className="text-gray-600">Cutting-edge ChistyLOS technology for advanced water purification and restoration.</p>
                    </div>

                    <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-amber-600">
                        <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Soil Remediation</h3>
                        <p className="text-gray-600">Activated charcoal-based soil remediation techniques for land restoration.</p>
                    </div>

                    <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-green-500">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H16c-.8 0-1.54.37-2.01 1l-2.99 3.99V22h-2v-9.01L8.01 9C7.54 8.37 6.8 8 6 8H3.46c-.8 0-1.54.37-2.01 1L1 16.5V22h2v-6h2.5l2.54-7.63A1.5 1.5 0 0 1 9.46 8H12c.8 0 1.54.37 2.01 1L17 13.99V22h3z"/>
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Green Hubs</h3>
                        <p className="text-gray-600">Strong network of green hubs supporting community development and sustainability.</p>
                    </div>
                </div>

                <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">
                                Transforming Crisis into Opportunity
                            </h3>
                            <p className="text-lg text-gray-600 leading-relaxed mb-6">
                                This project delivers more than clean water and fertile land—it drives rural employment, 
                                enhances food security, and lays the foundation for climate-smart communities. 
                                It's a scalable, closed-loop model for eco-restoration that turns crisis into opportunity.
                            </p>
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-500 uppercase">Starting Point</p>
                                    <p className="text-lg font-bold text-gray-900">Pra River & Tributaries</p>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <ImageSlider 
                                images={ecoReviveImages}
                                className="w-full h-80"
                                showIndicators={true}
                                autoPlay={true}
                                interval={5000}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
} 